import React, { createContext, useContext, useEffect, useState } from 'react';
import api from '../lib/api';
import { onHealthDataUpdated } from '../lib/healthSync';
import { setBiometricSessionUnlocked, setBiometricUnlockEnabled } from '../lib/nativeBiometrics';

const AuthContext = createContext(null);
const BOOTSTRAP_TIMEOUT_MS = 12000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  const [profile, setProfile] = useState(null);
  const [doctor, setDoctor] = useState(null)
  const [assessments, setAssessments] = useState([]);
  const [isBootstrapping, setIsBootstrapping] = useState(true);

  useEffect(() => {
    let mounted = true;
    let bootstrapSettled = false;
    let timeoutId;

    const finishBootstrap = () => {
      if (!mounted || bootstrapSettled) return;
      bootstrapSettled = true;
      setIsBootstrapping(false);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };

    async function bootstrap() {
      setIsBootstrapping(true);

      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          throw new Error('No saved auth token');
        }

        const res = await api.get('/api/auth/me');
        const verifiedUser = res?.data?.user;
        if (!verifiedUser?._id && !verifiedUser?.id) {
          throw new Error('Invalid saved session');
        }

        const normalizedVerifiedUser = {
          ...verifiedUser,
          _id: verifiedUser._id || verifiedUser.id,
          id: verifiedUser.id || verifiedUser._id,
        };

        setUser(normalizedVerifiedUser);
        localStorage.setItem('user', JSON.stringify(normalizedVerifiedUser));
        localStorage.setItem('userId', normalizedVerifiedUser._id);
        await checkAndSetRecordFlags();
        return;

      } catch (err) {
        if (err?.message !== 'No saved auth token') {
          console.error('Bootstrap error:', err);
        }
        setUser(null);
        setProfile(null);
        setAssessments([]);
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        localStorage.removeItem('userId');
        localStorage.setItem('HAS_PROFILE', 'false');
        localStorage.setItem('HAS_ASSESSMENT', 'false');
      } finally {
        finishBootstrap();
      }
    }

    timeoutId = setTimeout(() => {
      console.warn('Auth bootstrap timed out; continuing with fallback navigation.');
      finishBootstrap();
    }, BOOTSTRAP_TIMEOUT_MS);

    bootstrap();
    return () => {
      mounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  const finalizeAuth = async (responseData) => {
    const rawUserData = responseData?.user || responseData;
    const token = responseData?.token;
    const userData = rawUserData
      ? {
          ...rawUserData,
          _id: rawUserData._id || rawUserData.id,
          id: rawUserData.id || rawUserData._id,
        }
      : rawUserData;

    if (!token) {
      throw new Error('Authentication token missing from server response');
    }

    localStorage.setItem('authToken', token);
    setUser(userData);
    if (userData?._id) {
      localStorage.setItem('userId', userData._id);
    }
    localStorage.setItem('user', JSON.stringify(userData));
    setBiometricSessionUnlocked(true);
    await checkAndSetRecordFlags();
    return userData;
  };

  const signin = async ({ email, password, enableBiometricUnlock = false }) => {

    const res = await api.post('/api/auth/signin', { email, password });
    const userData = await finalizeAuth(res.data);
    setBiometricUnlockEnabled(enableBiometricUnlock);

    // Log activity
    try {
      await api.post('/api/activities/add-activity', { message: 'to the app' });
    } catch (err) {
      console.error('Failed to log signin activity', err);
    }

    return userData;
    
  };

  const signup = async ({ fullName, email, password }) => {

    const res = await api.post('/api/auth/signup', { fullName, email, password });
    const userData = await finalizeAuth(res.data);

    // Log activity
    try {
      await api.post('/api/activities/add-activity', { message: 'Signed up for the app' });
    } catch (err) {
      console.error('Failed to log signup activity', err);
    }

    return userData;
  };

  const signinWithGoogle = async (googleToken, tokenType = 'id_token') => {
    try {
      if (!googleToken) {
        throw new Error('Google credential is missing. Please try again.');
      }

      const payload = tokenType === 'access_token'
        ? { accessToken: googleToken }
        : { credential: googleToken, idToken: googleToken };

      const res = await api.post('/api/auth/google', payload);
      const userData = await finalizeAuth(res.data);

      try {
        await api.post('/api/activities/add-activity', {
          message: res.data?.created ? 'Signed up with Google' : 'with Google',
        });
      } catch (err) {
        console.error('Failed to log Google auth activity', err);
      }

      return {
        user: userData,
        created: Boolean(res.data?.created),
      };
    } catch (error) {
      console.error('Google sign-in failed:', {
        code: error?.code || '',
        message: error?.message,
      });

      throw error;
    }
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch {}
    setUser(null);
    setProfile(null);
    setAssessments([]);
    setBiometricSessionUnlocked(false);
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('userId');
    localStorage.setItem('HAS_PROFILE', 'false');
    localStorage.setItem('HAS_ASSESSMENT', 'false');
  };

  async function checkAndSetRecordFlags() {
    let hasProfile = false;
    let hasAssessment = false;
    try {
      const [p, a] = await Promise.allSettled([
        api.get('/api/profile'),
        api.get('/api/assessment'),
      ]);
      if (p.status === 'fulfilled' && p.value?.data?.profile) {
        hasProfile = true;
        setProfile(p.value.data.profile);
      }
      if (a.status === 'fulfilled' && Array.isArray(a.value?.data?.assessments) && a.value.data.assessments.length > 0) {
        // Sometimes duplicate or partial assessment documents exist. Consider an assessment
        // "completed" if it contains meaningful data (a score, fullName or basic metrics).
        const rawList = a.value.data.assessments;
        const completed = rawList.some((as) => {
          if (!as || typeof as !== 'object') return false;
          if (typeof as.score === 'number' && !Number.isNaN(as.score)) return true;
          if (as.fullName && String(as.fullName).trim().length > 0) return true;
          if ((as.weightKg || as.weight || as.heightCm || as.height) && (as.weightKg > 0 || as.weight > 0 || as.heightCm > 0 || as.height > 0)) return true;
          // fallback: treat any document with several keys as meaningful
          const keys = Object.keys(as || {});
          return keys.length >= 3;
        });
        if (completed) {
          hasAssessment = true;
        }
        setAssessments(rawList);
      }
    } catch {}
    localStorage.setItem('HAS_PROFILE', hasProfile ? 'true' : 'false');
    localStorage.setItem('HAS_ASSESSMENT', hasAssessment ? 'true' : 'false');
    return { hasProfile, hasAssessment };
  }

  async function refreshCompletionFlags() {
    return checkAndSetRecordFlags();
  }

  async function fetchProfile() {
    try {
      const res = await api.get('/api/profile');
      const p = res?.data?.profile ?? null;
      setProfile(p);
      localStorage.setItem('HAS_PROFILE', p ? 'true' : 'false');
      return p;
    } catch {
      setProfile(null);
      localStorage.setItem('HAS_PROFILE', 'false');
      return null;
    }
  }

  async function fetchAssessments() {
    try {
      const res = await api.get('/api/assessment');
      const a = res?.data?.assessments ?? [];
      setAssessments(a);
      localStorage.setItem('HAS_ASSESSMENT', a && a.length ? 'true' : 'false');
      return a;
    } catch {
      setAssessments([]);
      localStorage.setItem('HAS_ASSESSMENT', 'false');
      return [];
    }
  }

  useEffect(() => {
    const unsubscribe = onHealthDataUpdated((detail = {}) => {
      const source = String(detail?.source || '');
      if (!source || source.startsWith('assessment:')) {
        fetchAssessments();
      }
    }, { debounceMs: 250 });

    return unsubscribe;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        signin,
        signup,
        signinWithGoogle,
        logout,
        profile,
        assessments,
        fetchProfile,
        fetchAssessments,
        refreshCompletionFlags,
        isBootstrapping,
        setBiometricUnlockEnabled,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
